import React, { useState, useRef, useEffect } from "react";
import {
  CarWrapper,
  LocationWrapper,
  HoverWindow,
  Marker,
} from "commonComponents/HoleUI/CarsOnCourseStyled";
import { ReactComponent as OutOfServiceIcon } from "assets/svgIcons/MapLegendPopup/Status/Outofservice.svg";
import { isActionZoneAlert } from "_helperFunctions/isActionZoneAlert";
import { useTranslation } from "react-i18next";
import CarIcon from "../../assets/svgIcons/CarIcon";
import FlagIcon from "../../assets/svgIcons/FlagIcon";
import CloseIcon from "assets/svgIcons/ModalCloseOutIcon";
import BatteryIconWithCircle from "assets/svgIcons/BatteryIconWithCircle";
import BatteryIconWithCircleRed from "assets/svgIcons/BatteryIconWithCircleRed";
import MarshalCarIcon from "../../assets/svgIcons/CarType/Marshals";
import SpecialNeedCarIcons from "../../assets/svgIcons/CarType/SpecialNeeds";
import FoodandBewragesCarIcon from "../../assets/svgIcons/CarType/F&B";
import ChatIcon from "assets/svgIcons/ChatIcon";
import ExclamationMarkWhiteIcon from "assets/svgIcons/ExclamationMarkIconWhite";
import ExclamationMarkWithRedBg from "assets/svgIcons/ExclamationMarkWithRedBg";
import TagIcon from "assets/svgIcons/LocationPointIcon";
import LocationPointIcon from "assets/svgIcons/ShareIcon";
import MapMarkerIcon from "assets/svgIcons/MapMarkerIcon";
import { useHookstate } from "@hookstate/core";
import {
  MAP_GLOBAL_STATES,
  FLEET_GLOBAL_STATES,
  SITEID_GLOBAL_STATE,
  SITELOCATION_GLOBAL_STATE,
  CAR_LOCATION_STATE,
  CAR_NUMBER_MESSAGE,
  SiteSetupAction,
  UPDATED_CAR_DATA_STATE,
} from "globalStore/globalStore";

// for car property window import
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import ShowCenterIcon from "assets/svgIcons/ShowCenterIcon";
import ShowPointIcon from "assets/svgIcons/MapArrowMarkerIcon";
import {
  Tooltip,
} from "@mui/material";
import { getTextColorBasedOnRGB, teeMarkerColor } from "./teeMarkerColor";
import { mapHelpers } from "pages/Pins/utils/mapHelpers";
import { isStale } from "_helperFunctions/isStale";
import SignalIcon from "assets/svgIcons/SignalIcon";
import { COMMON_SERVICES } from "redux/reduxToolkitServices/_commonServices/commonServices";
import { reasonForReportString, carType as constCarType } from "constant";
import { getPaceString } from "./functions/getPaceString";

const MAP_ICON_COLORS = {
  PACE_WARNING: "#EAB52D",
  PACE_CRITICAL: "#FA3C30",
  PACE_NORMAL: "#F2F0E6", // Alabaster (old color was #947550)
  POI_APPROACH: "#5E4DC3",
  POI_HOLE: "#483D8B"
};

const MapCenterMarker = (props) => {
  const { uniqueId } = props;
  const { enableClickToDriveFeature } = useHookstate(MAP_GLOBAL_STATES);
  return (
    <Marker
      key={uniqueId}
      className="marker"
      style={{ cursor: !enableClickToDriveFeature.get() && "pointer" }}
      onClick={props.handleSitsetupWindow}
    >
      <ShowCenterIcon />
    </Marker>
  );
};

const MapPointMarker = (props) => {
  const { uniqueId, type, position } = props;
  const { enableClickToDriveFeature } = useHookstate(MAP_GLOBAL_STATES);

  const renderPoiIcons = () => { // the white arrows inside the markers
    if (position === 0) {
      return (
        <ShowPointIcon
          style={{
            transform: "rotate(90deg)",
            position: "relative",
            zIndex: "1",
          }}
        />
      );
    } else if (position === 1) {
      return (
        <ShowPointIcon
          style={{
            transform: "rotate(270deg)",
            position: "relative",
            zIndex: "1",
          }}
        />
      );
    } else if (position === 2) {
      return (
        <ShowPointIcon
          style={{
            transform: "rotate(180deg)",
            position: "relative",
            zIndex: "1",
          }}
        />
      );
    } else if (position === 3) {
      return (
        <ShowPointIcon
          style={{ position: "relative", zIndex: "1" }}
        />
      );
    }
  };

  return (
    <div
      key={uniqueId}
      className="marker"
      style={{
        cursor: !enableClickToDriveFeature.get() && "pointer",
        position: "relative",
      }}
      onClick={props.handleSitsetupWindow}
    >
      {renderPoiIcons()}
      <MapMarkerIcon
        color={type === 2 ? MAP_ICON_COLORS.POI_HOLE : MAP_ICON_COLORS.POI_APPROACH}
        style={{
          position: "absolute",
          top: "-9px",
          left: "-9px",
          right: "0",
          zIndex: "0",
          color: type === 2 ? MAP_ICON_COLORS.POI_HOLE : MAP_ICON_COLORS.POI_APPROACH,
        }}
      />
    </div>
  );
};

const MapTeesMarker = (props) => {
  const { enableClickToDriveFeature } = useHookstate(MAP_GLOBAL_STATES);
  const { uniqueId, holeNumber, teeMarkerSeetingId } = props;

  const { siteSetupData } = useSelector((state) => state.commonReducers) || [];

  return (
    <div
      key={uniqueId}
      className="marker"
      style={{
        cursor: !enableClickToDriveFeature.get() && "pointer",
        position: "relative",
      }}
      onClick={props.handleSitsetupWindow}
    >
      <MapMarkerIcon
        color={teeMarkerColor(siteSetupData, teeMarkerSeetingId)}
      />
      <p
        style={{
          backgroundColor: teeMarkerColor(siteSetupData, teeMarkerSeetingId),
          width: "23px",
          height: "19px",
          borderRadius: "5px",
          fontSize: "16px",
          lineHeight: "19px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          position: "absolute",
          bottom: "13px",
          color: getTextColorBasedOnRGB(siteSetupData, teeMarkerSeetingId),
        }}
      >
        {holeNumber}
      </p>
    </div>
  );
};

const MapHoleMarker = (props) => {
  const { uniqueId, courseNumber, holeNumber } = props;

  const { siteSettingsData } =
    useSelector((state) => state.commonReducers) || [];
  return (
    <div
      key={uniqueId}
      className="marker"
      style={{
        backgroundColor:
          siteSettingsData &&
          mapHelpers.CourseBGColor(siteSettingsData, courseNumber),
        width: 22,
        height: 22,
        borderRadius: 50,
        fontSize: "13px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        color:
          siteSettingsData &&
          mapHelpers.CourseTxtColor(siteSettingsData, courseNumber),
        fontWeight: "bold",
        border: "1px solid",
        borderColor:
          courseNumber === 1 ? "#fff" : courseNumber === 2 ? "#000" : "white",
      }}
    >
      {holeNumber}
    </div>
  );
};

export const NewMapPointMarker = (props) => {
  const { uniqueId, type, position } = props;
  const { enableClickToDriveFeature } = useHookstate(MAP_GLOBAL_STATES);
  const renderPoiIcons = () => { // the white arrows inside the markers
    if (position === 0) {
      return (
        <ShowPointIcon
          style={{ transform: "rotate(90deg)" }}
        />
      );
    } else if (position === 1) {
      return (
        <ShowPointIcon
          style={{ transform: "rotate(270deg)" }}
        />
      );
    } else if (position === 2) {
      return (
        <ShowPointIcon
          style={{ transform: "rotate(180deg)" }}
        />
      );
    } else if (position === 3) {
      return <ShowPointIcon />;
    }
  };

  return (
    <div
      key={uniqueId}
      className="marker"
      style={{ cursor: !enableClickToDriveFeature.get() && "pointer" }}
    >
      {renderPoiIcons()}
      <MapMarkerIcon
        color={type === 2 ? MAP_ICON_COLORS.POI_HOLE : MAP_ICON_COLORS.POI_APPROACH}
        style={{
          position: "absolute",
          top: "-9px",
          left: "-9px",
          right: "0",
          zIndex: "0",
          color: type === 2 ? MAP_ICON_COLORS.POI_HOLE : MAP_ICON_COLORS.POI_APPROACH,
        }}
      />
    </div>
  );
};

export const NewMapTeesMarker = (props) => {
  const { uniqueId, teeMarkerSeetingId, holeNumber } = props;
  const { enableClickToDriveFeature } = useHookstate(MAP_GLOBAL_STATES);
  const { siteSetupData } = useSelector((state) => state.commonReducers) || [];
  return (
    <div
      key={uniqueId}
      className="marker"
      style={{ cursor: !enableClickToDriveFeature.get() && "pointer", position: "relative", }}
    >
      <MapMarkerIcon
        color={teeMarkerColor(siteSetupData, teeMarkerSeetingId)}
      />
      <p
        style={{
          backgroundColor: teeMarkerColor(siteSetupData, teeMarkerSeetingId),
          width: "23px",
          height: "19px",
          borderRadius: "5px",
          fontSize: "16px",
          lineHeight: "19px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          position: "absolute",
          bottom: "13px",
          color: getTextColorBasedOnRGB(siteSetupData, teeMarkerSeetingId),
        }}
      >
        {holeNumber}
      </p>
    </div>
  );
};

export const LocationIcon = ({ Id, locationName, IsEUSite }) => {
  const { siteId, selectSite, hideSubNav } = useHookstate(SITEID_GLOBAL_STATE);
  const { siteLocationGlobalData, singleSiteLocationData, recentSiteState } =
    useHookstate(SITELOCATION_GLOBAL_STATE);
  const { mapTypeHandler, prevMapTypeValue } = useHookstate(MAP_GLOBAL_STATES);
  const { hideAll } = useHookstate(SiteSetupAction);

  const locationZoom = () => {
    siteId.set(Id);
    hideSubNav.set(true);
    recentSiteState.set(false);
    mapTypeHandler.set(prevMapTypeValue.get());
    localStorage.setItem("recentSite", Id);
    siteLocationGlobalData.set(false);
    singleSiteLocationData.set(true);
    selectSite.set(false);
    hideAll.set(false);
  };
  return (
    <LocationWrapper key={Id}>
      <div
        title={locationName + " - " + Id}
        onClick={(e) => locationZoom(e)}
        className="container1"
        id={Id}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            lineHeight: "23px",
          }}
        >
          <button
            style={{
              background: IsEUSite ? "#FFD500" : "#ffffff",
            }}
          >
            <FlagIcon color={"#000000"} />
          </button>
        </div>
      </div>
    </LocationWrapper>
  );
};

const MapCarIcon = React.memo(
  ({
    car,
    uniqueId,
    carNumber,
    carType,
    holeNumber,
    holeLocation,
    batteryLevel,
    GpsiCarId,
    carnumber,
    carsPosition,
    setCarsPosition,
    zoomPosition,
    setEditModel,
    secondaryCarType,
    memberName
  }) => {
    const {
      showMessageBoxForSingleCar,
      getSingleCarData,
      tagCarNumber,
      highlightTagCar,
      carForPopup,
      taggedCarsSaverGlobalState,
      carDetailGpsiCarId,
      carNumberHover
    } = useHookstate(MAP_GLOBAL_STATES);

    const { siteId } = useHookstate(SITEID_GLOBAL_STATE);

    const { updatedCarData } = useHookstate(UPDATED_CAR_DATA_STATE);
    const {
      siteSettingsData,
      getCarPathActionZone,
      getCarTrackData,
    } = useSelector((state) => state.commonReducers) || [];

    const { CarLocationBtn } = useHookstate(CAR_LOCATION_STATE);
    const ref = useRef(null);
    const dispatch = useDispatch();

    let carRef = useRef(carNumber);
    const carPopupRef = useRef(null);
    const carPopupClickRef = useRef(null);

    const { t } = useTranslation();

    const [hoverState, setHoverState] = useState(false);

    useEffect(() => {
      const localData = JSON.parse(localStorage.getItem("taggedCarsData"));
      if (localData) {
        taggedCarsSaverGlobalState.set(localData.tagCarsDataMap);
        showTaggedCarArray.set(localData.tagCarsDataFilters);
        taggedCarsDisplayerGlobalState.set(
          localData.taggedCarsDisplayerGlobalState
        );
      }
    }, []);

    useEffect(() => {
      const parsedCarData = JSON.parse(updatedCarData.get())?.length
        ? JSON.parse(updatedCarData.get())
        : [];
      if (
        Array.isArray(parsedCarData) &&
        parsedCarData &&
        parsedCarData.length > 0 &&
        carDetailGpsiCarId.get()
      ) {
        const selectedCar = parsedCarData.find(
          (item) => item?.Car?.GpsiCarId === carDetailGpsiCarId.get()
        );
        if (selectedCar) {
          carForPopup.set(selectedCar);
        }
      } else {
        carForPopup.set({});
      }
    }, [updatedCarData.get(), carDetailGpsiCarId.get()]);

    const setCarsData = (e) => {
      e.stopPropagation();
      setCarNumber.set(carNumber);
      getSingleCarData.set({ GpsiCarId: GpsiCarId, CarNumber: carNumber });
      carsGpsSelectorData.set([GpsiCarId]);
      carsForMessagesGetter.set([carNumber]);
      showMessageBoxForSingleCar.set(true);
      clickWindowState.set(false);
      clickWindowStateCarNumber.set(null);
    };

    const setTagCarNumber = (carnumber) => {
      if (tagCarNumber.get().length == 0) {
        tagCarNumber.set((tasks) => [...tasks, carnumber]);
      } else {
        const exists = tagCarNumber.get().find((item) => item == carnumber);
        if (exists) {
          tagCarNumber.set((tasks) =>
            tasks.filter((task) => task !== carnumber)
          );
        } else {
          tagCarNumber.set((tasks) => [...tasks, carnumber]);
        }
      }

      const localStorageData = JSON.parse(
        localStorage.getItem("taggedCarsData")
      );
      const showTaggedCarArrayExist = showTaggedCarArray
        .get()
        .find((item) => item === carnumber);
      if (showTaggedCarArrayExist) {
        showTaggedCarArray.set((tasks) =>
          tasks.filter((task) => task !== carnumber)
        );
        highlightTagCar.set(false);
      } else {
        showTaggedCarArray.set((tasks) => [...tasks, carnumber]);
        highlightTagCar.set(true);
      }

      const taggedCarsSaverGlobalStateExist = taggedCarsSaverGlobalState
        .get()
        .find((item) => item === carnumber);
      if (taggedCarsSaverGlobalStateExist) {
        taggedCarsSaverGlobalState.set((tasks) =>
          tasks.filter((task) => task !== carnumber)
        );
        taggedCarsDisplayerGlobalState.set(false);
        highlightTagCar.set(false);
      } else {
        taggedCarsSaverGlobalState.set((tasks) => [...tasks, carnumber]);
        taggedCarsDisplayerGlobalState.set(true);
        highlightTagCar.set(true);
      }

      if (localStorageData) {
        localStorage.setItem(
          "taggedCarsData",
          JSON.stringify({
            findCars: true,
            taggedCarsDisplayerGlobalState:
              taggedCarsDisplayerGlobalState.get(),
            tagCarsDataMap: taggedCarsSaverGlobalState.get(),
            tagCarsDataFilters: showTaggedCarArray.get(),
          })
        );
      } else {
        localStorage.setItem(
          "taggedCarsData",
          JSON.stringify({
            findCars: true,
            taggedCarsDisplayerGlobalState:
              taggedCarsDisplayerGlobalState.get(),
            tagCarsDataMap: taggedCarsSaverGlobalState.get(),
            tagCarsDataFilters: showTaggedCarArray.get(),
          })
        );
      }

      clickWindowState.set(false);
    };

    const MakeHighlightTagOnMenu = (carNumber) => {
      let finded = taggedCarsSaverGlobalState
        .get()
        .find((item) => item === carNumber);
      if (!finded) {
        return false;
      } else {
        return true;
      }
    };

    function paceInfoBorder(detector) {
      if (detector === "border") {
        if (
          car?.Car !== undefined &&
          car?.Car !== null &&
          Object.keys(car?.Car).length !== 0
        ) {
          const paceWarning = car?.Car ? car?.Car?.PaceStatus === 2 : null;
          const paceCritical = car?.Car ? car?.Car?.PaceStatus === 3 : null;
          if (paceWarning) {
            return MAP_ICON_COLORS.PACE_WARNING;
          }
          if (paceCritical) {
            return MAP_ICON_COLORS.PACE_CRITICAL;
          }

          return null;
        }
      } else if (detector === "car") { // doesn't look like paceInfoBorder("car") is ever called
        if (
          car?.Car !== undefined &&
          car?.Car !== null &&
          Object.keys(car?.Car).length !== 0
        ) {
          const paceWarning = car?.Car ? car?.Car?.PaceStatus === 2 : null;
          const paceCritical = car?.Car ? car?.Car?.PaceStatus === 3 : null;

          let result = "";
          if (paceWarning && paceCritical) {
            if (paceWarning === 2) {
              result = `1px solid ${MAP_ICON_COLORS.PACE_WARNING}`;
            } else if (paceCritical === 3) {
              result = `1px solid ${MAP_ICON_COLORS.PACE_CRITICAL}`;
            } else {
              result = `1px solid ${MAP_ICON_COLORS.PACE_NORMAL}`;
            }
          }
          return result;
        }
      } else {
        return <></>;
      }
    }

    const {
      taggedCarsDisplayerGlobalState,
      showCarDetails,
      clickWindowState,
      clickWindowStateCarNumber,
      showTaggedCarArray,
      TagCar,
      clientXOnClick,
      clientYOnClick,
      specificCarTrackHistory,
      showTaggedCar,
      displayLocation,
      carPopupClickEvent
    } = useHookstate(MAP_GLOBAL_STATES);

    const showWindow = (e, carNumber) => {
      setEditModel(false);
      carPopupClickEvent.set(e.target);
      clientXOnClick.set(e.clientX);
      clientYOnClick.set(e.clientY);
      if (hoverState) {
        setHoverState(false);
        clickWindowState.set(true);
        clickWindowStateCarNumber.set(carNumber);
        carNumberHover.set(true);
      } else {
        clickWindowState.set(true);
        clickWindowStateCarNumber.set(carNumber);
        carNumberHover.set(true);
      }
      if (carPopupRef.current) {
        carPopupRef.current.style.visibility = "hidden";
      }
    };

    const handleShowDetail = () => {
      showCarDetails.set(true);
      let temp = JSON.parse(JSON.stringify(car));
      carDetailGpsiCarId.set(temp.Car.GpsiCarId);
      carForPopup.set(temp);
    };

    function checkStyle() {
      if (tagCarNumber.get().includes(carnumber)) {
        return "tag-car container";
      }
      if (TagCar.get() === carnumber) {
        return "tag-car container";
      }
    }

    useEffect(() => {
      ref.current.className = " container";
      taggedCarsSaverGlobalState.get().map((item) => {
        const carElement = document.getElementById("id" + item);
        if (carElement) carElement.className = "tag-car container";
      });
    }, [taggedCarsSaverGlobalState.get()]);

    const { setCarNumber, carsGpsSelectorData, carsForMessagesGetter } =
      useHookstate(CAR_NUMBER_MESSAGE);

    const [plus, setPlus] = useState(false);
    const [overlappingDisplay, setOverlappingDisplay] = useState([]);
    const [zoomPositionUpdated, setZoomPositionUpdated] = useState(false);

    useEffect(() => {
      setZoomPositionUpdated(true);
    }, [zoomPosition]);

    useEffect(() => {
      if (zoomPositionUpdated) {
        carsPosition.forEach((data) => {
          data.overlappingData = [];
        });
        setZoomPositionUpdated(false);
      }

      // Collecting position of current car
      let carNumberFromID = Number(ref.current.id.replace("id", ""));
      let top = ref.current.getBoundingClientRect().top;
      let bottom = ref.current.getBoundingClientRect().bottom;
      let left = ref.current.getBoundingClientRect().left;
      let right = ref.current.getBoundingClientRect().right;

      //Check current car available in list
      let carIndex = carsPosition.findIndex(
        (item) => item.carNumber === carNumberFromID
      );

      // If available in carsPosition array check duplicate position details and update
      // Else add new
      if (carIndex !== -1) {
        if (
          carsPosition[carIndex].carPosition.top !== top ||
          carsPosition[carIndex].carPosition.bottom !== bottom ||
          carsPosition[carIndex].carPosition.left !== left ||
          carsPosition[carIndex].carPosition.right !== right
        ) {
          carsPosition[carIndex] = {
            carNumber: carNumberFromID,
            carPosition: {
              top: top,
              bottom: bottom,
              left: left,
              right: right,
            },
            overlapping: carsPosition[carIndex].overlapping,
            overlappingData: [...carsPosition[carIndex].overlappingData],
            car: car,
            holeNumber: holeNumber,
            holeLocation: holeLocation,
          };
          setCarsPosition([...carsPosition]);
        }
      } else {
        setCarsPosition([
          ...carsPosition,
          {
            carNumber: carNumberFromID,
            carPosition: {
              top: top,
              bottom: bottom,
              left: left,
              right: right,
            },
            overlapping: false,
            overlappingData: [carNumberFromID],
          },
        ]);
      }

      //Checking for overlapping
      if (carsPosition.length > 0) {
        let finded = carsPosition.findIndex((item) => {
          let areaOfCurrent = (right - left) * (bottom - top);
          let areaOfItem =
            (item.carPosition.right - item.carPosition.left) *
            (item.carPosition.bottom - item.carPosition.top);
          let area = areaOfCurrent <= areaOfItem ? areaOfCurrent : areaOfItem;
          // let area = (right - left) * (bottom - top);

          let percentage75 = area * 0.75;
          let percentage50 = area * 0.5;

          let XA1 = left;
          let YA1 = top;

          let XA2 = right;
          let YA2 = bottom;

          let XB1 = item.carPosition.left;
          let YB1 = item.carPosition.top;

          let XB2 = item.carPosition.right;
          let YB2 = item.carPosition.bottom;

          let intersectingArea =
            Math.max(0, Math.min(XA2, XB2) - Math.max(XA1, XB1)) *
            Math.max(0, Math.min(YA2, YB2) - Math.max(YA1, YB1));

          if (left > item.carPosition.right || right < item.carPosition.left) {
            return false;
          } //overlap not possible
          if (top > item.carPosition.bottom || bottom < item.carPosition.top) {
            return false;
          } //overlap not possible

          if (left > item.carPosition.left && left < item.carPosition.right) {
            if (intersectingArea > percentage50) return true;
          }
          if (right > item.carPosition.left && right < item.carPosition.right) {
            if (intersectingArea > percentage50) return true;
          }

          if (top > item.carPosition.top && top < item.carPosition.bottom) {
            if (intersectingArea > percentage50) return true;
          }
          if (
            bottom > item.carPosition.top &&
            bottom < item.carPosition.bottom
          ) {
            if (intersectingArea > percentage50) return true;
          }

          return false;
        });

        //if overlapping finded it will add particular car in OverlappingData property
        // else remove all cars and put the same car
        if (finded !== -1) {
          let tempOverlappingData = [];

          tempOverlappingData = carsPosition[finded].overlappingData;
          tempOverlappingData = [
            carsPosition[finded].carNumber,
            carNumberFromID,
            ...tempOverlappingData,
          ];
          let uniqueArray = [...new Set([...tempOverlappingData])];

          carsPosition[finded] = {
            ...carsPosition[finded],
            overlapping: true,
            overlappingData: [...uniqueArray],
          };
          let carNumberFromIDIndex = carsPosition.findIndex(
            (item) => item.carNumber === carNumberFromID
          );
          if (carNumberFromIDIndex !== -1) {
            if (!carsPosition[carNumberFromIDIndex].overlapping) {
              let tempOverlappingData = [];

              tempOverlappingData =
                carsPosition[carNumberFromIDIndex].overlappingData;
              tempOverlappingData = [
                carsPosition[carNumberFromIDIndex].carNumber,
                carNumberFromID,
                ...tempOverlappingData,
              ];
              let uniqueArray = [...new Set([...tempOverlappingData])];

              carsPosition[carNumberFromIDIndex] = {
                ...carsPosition[carNumberFromIDIndex],
                overlapping: true,
                overlappingData: [...uniqueArray],
              };
            }
          }
        } else {
          let findedNotOverlapping = carsPosition.findIndex(
            (item) => carNumberFromID === item.carNumber
          );
          if (findedNotOverlapping !== -1) {
            if (carsPosition[findedNotOverlapping].overlapping) {
              carsPosition[findedNotOverlapping] = {
                ...carsPosition[findedNotOverlapping],
                overlappingData: [carNumberFromID],
                overlapping: false,
              };

              carsPosition.map((data) => {
                let noOverlap = data.overlappingData.findIndex(
                  (data1) =>
                    data.carNumber !== carNumberFromID &&
                    data1 === carNumberFromID
                );
                if (noOverlap !== -1) data.overlappingData.splice(noOverlap, 1);
              });
            }
          }
        }

        let overlappingFinded = carsPosition.find((item) => {
          return (
            item.carNumber === carNumber &&
            item.overlapping &&
            item.overlappingData.length > 0
          );
        });

        if (overlappingFinded) {
          let indexArray = [];
          overlappingFinded.overlappingData.map((item) => {
            let index = carsPosition.findIndex(
              (item1) => item1.carNumber === item
            );
            if (index !== -1) indexArray.push(index);
          });
          if (indexArray.length > 1) {
            let min = Math.min(...indexArray);
            if (carsPosition[min].carNumber === carNumber) {
              setPlus(true);
            } else {
              setPlus(false);
            }
          }
        } else {
          setPlus(false);
        }
      }
    });

    useEffect(() => {
      let tempOverlappingDisplay = [];
      carsPosition.map((item) => {
        if (item.carNumber === carNumber) {
          if (plus) {
            item.overlappingData.map((item1) => {
              let currentCar = carsPosition.find(
                (item2) => item2.carNumber === item1
              );
              if (currentCar) {
                tempOverlappingDisplay.push({
                  carNumber: currentCar.carNumber,
                  car: currentCar.car,
                  holeNumber: currentCar.holeNumber,
                  holeLocation: currentCar.holeLocation,
                });
                setOverlappingDisplay([...tempOverlappingDisplay]);
              }
            });
          } else {
            tempOverlappingDisplay.push({
              carNumber: item.carNumber,
              car: item.car,
              holeNumber: item.holeNumber,
              holeLocation: item.holeLocation,
            });
            setOverlappingDisplay([...tempOverlappingDisplay]);
          }
        }
      });
    }, [plus, carsPosition, hoverState]);

    const [carPopupClass, setCarPopupClass] = useState("");

    useEffect(() => {
      if (clickWindowState.get()) fixPosition(null);
    }, [carPopupClass]);

    const fixPosition = (e) => {
      let xValue = 0;
      let yValue = 0;
      if (clickWindowState.get()) {
        xValue = clientXOnClick.get();
        yValue = clientYOnClick.get();
      } else {
        xValue = e.clientX;
        yValue = e.clientY;
      }

      if (yValue < 230 && xValue + 135 > window.innerWidth)
        //top-right side overflow
        setCarPopupClass("bottom-left");
      else if (yValue < 230 && xValue - 175 < 0)
        // top-left side overflow
        setCarPopupClass("bottom-right");
      else if (
        yValue + 230 > window.innerHeight &&
        xValue + 135 > window.innerWidth
      )
        // bottom-right side overflow
        setCarPopupClass("top-left");
      else if (yValue + 230 > window.innerHeight && xValue - 175 < 0)
        // bottom-left side overflow
        setCarPopupClass("top-right");
      else if (xValue + 135 > window.innerWidth)
        //right side overflow clientY
        setCarPopupClass("for-left");
      else if (xValue - 175 < 0)
        // left side overflow
        setCarPopupClass("for-right");
      else if (yValue + 230 > window.innerHeight)
        // bottom side overflow
        setCarPopupClass("for-top");
      else setCarPopupClass("");
    };

    const mouseAction = (e, action) => {
      // above code will not allow the tee markers to edit
      if (action === "enter" && carPopupRef.current?.style?.visibility !== "visible") {
        fixPosition(e);

        if (carPopupRef?.current) carPopupRef.current.style.visibility = "visible";

        setHoverState(true);
      } else if (carPopupRef.current?.style?.visibility !== "hidden") {
        if (carPopupRef?.current) carPopupRef.current.style.visibility = "hidden";

        setHoverState(false);
      }
    };

    const setCarPopupClickEvent = (e) => {
      if (e !== null) {
        carPopupClickEvent.set(e.target);
      } else {
        carPopupClickEvent.set(e);
      }
    };

    return (
      <CarWrapper key={uniqueId}>
        <div
          ref={ref}
          id={"id" + carNumber}
          className={checkStyle()}
          style={{ position: "relative" }}
          onClick={(e) => showWindow(e, carNumber)}
          onMouseEnter={(e) => mouseAction(e, "enter")}
          onMouseLeave={(e) => mouseAction(e, "leave")}
        >
          {carType === constCarType.normal ? (
            <></>
          ) : carType === constCarType.marshal ? (
            <div
              className="car-icon"
              style={{
                border: paceInfoBorder("border")
                  ? `1px solid ${paceInfoBorder("border")}`
                  : `1px solid ${MAP_ICON_COLORS.PACE_NORMAL}`,
                color: paceInfoBorder("border")
                  ? `${paceInfoBorder("border")}`
                  : `${MAP_ICON_COLORS.PACE_NORMAL}`,
                zIndex: "2",
              }}
            >
              <MarshalCarIcon />
            </div>
          ) : carType === constCarType.handicap || (secondaryCarType === constCarType.handicap && carType === constCarType.member) ? (
            <div
              className="car-icon"
              style={{
                border: paceInfoBorder("border")
                  ? `1px solid ${paceInfoBorder("border")}`
                  : `1px solid ${MAP_ICON_COLORS.PACE_NORMAL}`,
                color: paceInfoBorder("border")
                  ? `${paceInfoBorder("border")}`
                  : `${MAP_ICON_COLORS.PACE_NORMAL}`,
                zIndex: "2",
              }}
            >
              <SpecialNeedCarIcons bg={"#0026AC"} />
            </div>
          ) : carType === constCarType.bevcart ? (
            <div
              className="car-icon"
              style={{
                border: paceInfoBorder("border")
                  ? `1px solid ${paceInfoBorder("border")}`
                  : `1px solid ${MAP_ICON_COLORS.PACE_NORMAL}`,
                color: paceInfoBorder("border")
                  ? `${paceInfoBorder("border")}`
                  : `${MAP_ICON_COLORS.PACE_NORMAL}`,
                zIndex: "2",
              }}
            >
              <FoodandBewragesCarIcon />
            </div>
          ) : carType === constCarType.outOfService ? (
            <div className="car-icon">
              <OutOfServiceIcon />
            </div>
          ) : (
            <></>
          )}
          <div
            className="car-number"
            style={{
              background: carType === constCarType.member ? "#2b8c12" : "#1d1d1d",
              border: paceInfoBorder("border")
                ? `1px solid ${paceInfoBorder("border")}`
                : `1px solid ${MAP_ICON_COLORS.PACE_NORMAL}`,
              color: paceInfoBorder("border")
                ? `${paceInfoBorder("border")}`
                : `${MAP_ICON_COLORS.PACE_NORMAL}`,
            }}
          >
            <div
              style={{
                color: paceInfoBorder("border")
                  ? `${paceInfoBorder("border")}`
                  : `${MAP_ICON_COLORS.PACE_NORMAL}`,
              }}
            >
              {carNumber}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  right: -1,
                }}
              >
                {plus && (!getCarTrackData.length || !CarLocationBtn.get()) && (
                  <>+</>
                )}
              </div>
            </div>

            {batteryLevel && batteryLevel <= 25 && batteryLevel > 10 ? (
              <div className="icon-wrapper">
                <BatteryIconWithCircle />
              </div>
            ) : batteryLevel <= 10 ? (
              <div className="icon-wrapper">
                <BatteryIconWithCircleRed />
              </div>
            ) : (
              <></>
            )}
            {/* check if car exceed max holes then showing excalamation icon status */}
            {isActionZoneAlert(getCarPathActionZone, car?.Car?.ActionZoneId) ||
              car?.Car?.MaxHolesExceeded ? (
              <div className="icon-wrapper">
                <ExclamationMarkWithRedBg />
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
        {/* Map car hover popup */}
        {clickWindowStateCarNumber.get() !== carnumber ? (
          <HoverWindow
            className={"carPopup " + carPopupClass}
            ref={carPopupRef}
          >
            <div className="SignalIconCover">
              <Tooltip title={t("This_car_is_connected_via_Wi-Fi")}>
                <div>
                  <SignalIcon
                    width="16px"
                    height="12.5px"
                    color={isStale(car?.Car) ? "gray" : "#5AADFF"}
                  />
                </div>
              </Tooltip>
            </div>
            {overlappingDisplay.map((item, i) => {
              return (
                <div className="wrapper" key={"carHovar" + i}>
                  <h3>Car: {item.carNumber}</h3>
                  <p className="date-and-time">
                    {moment
                      .utc(item?.car?.Car?.LastLocationUpdateInUtc)
                      .utcOffset(-siteSettingsData?.TimeZoneOffset)
                      .format("DD-MMM-YYYY, h:mm a")}
                  </p>
                  <p>
                    <span style={{ fontSize: 14 }}>
                      <span style={{ display: "block" }}>{memberName}</span>
                      {item?.car?.Car?.HoleLocation === 0
                        ? "Not playing"
                        : item?.car?.Car?.HoleLocation === 1
                          ? `Hole ${item?.car?.Car?.Hole} Between Holes `
                          : item?.car?.Car?.HoleLocation === 2
                            ? `Hole ${item?.car?.Car?.Hole} Tee`
                            : item?.car?.Car?.HoleLocation === 3
                              ? `Hole ${item?.car?.Car?.Hole} Fairway`
                              : item?.car?.Car?.HoleLocation === 4
                                ? `Hole ${item?.car?.Car?.Hole} Green`
                                : ""}
                    </span>
                    <br />
                    <span style={{ fontWeight: "bold" }}>
                      {t("Pace_Status")}: {" "}
                    </span>
                    <span style={{ fontSize: 14 }}>
                      {item?.car?.Car?.HoleLocation !== 0
                        ? getPaceString(
                          item.car?.Car?.Pace,
                          item.car?.Car?.HolesPlayed,
                          item.car?.Car?.CarType
                        )
                        : t("Unknown")}
                      <br />
                      <span>
                        {item.car?.Car?.PlayerName1
                          ? `Playername - ${item.car?.Car?.PlayerName1}`
                          : t("Player_1")}
                      </span>
                      <br />
                      <span>
                        {car?.Car?.PlayerName2
                          ? `Playername - ${item.car?.Car?.PlayerName2}`
                          : t("Player_2")}
                      </span>
                      <br />
                      <span>
                        {t("Report_Reason")} :{" "}
                        {reasonForReportString(
                          item.car?.Car?.LocationReasonForReport
                        )}
                      </span>
                      <br />
                      <span>
                        {t("GPRS_Signal")} :{" "}
                        {item.car?.Car?.LocationGprsSignalStrength}{" "}
                      </span>
                      <br />
                      <span>
                        {t("Sequence")} # :{" "}
                        {item.car?.Car?.LocationSequenceNumber}{" "}
                      </span>
                    </span>
                  </p>
                  <div className="arrow"></div>
                </div>
              );
            })}
          </HoverWindow>
        ) : (
          <></>
        )}

        {clickWindowState.get() &&
          clickWindowStateCarNumber.get() === carnumber ? (
          <>
            {!showCarDetails.get() && (
              <HoverWindow
                ref={carPopupClickRef}
                className={carPopupClass}
                onClick={(e) => {
                  setCarPopupClickEvent(e);
                }}
              >
                <div className="SignalIconCover">
                  <SignalIcon
                    width="16px"
                    height="12.5px"
                    color={isStale(car?.Car) ? "gray" : "#5AADFF"}
                  />
                </div>
                <div className="wrapper" ref={carRef}>
                  <h3>Car: {carNumber}</h3>
                  <CloseIcon
                    className="close-icon"
                    onClick={() => {
                      clickWindowState.set(false);
                      clickWindowStateCarNumber.set(null);
                      setTimeout(() => {
                        carNumberHover.set(false);
                        setCarPopupClickEvent(null);
                      }, 1000);
                    }}
                  />

                  <p className="date-and-time">
                    {moment
                      .utc(car?.Car?.LastLocationUpdateInUtc)
                      .utcOffset(-siteSettingsData?.TimeZoneOffset)
                      .format("DD-MMM-YYYY, h:mm a")}
                  </p>
                  <p>
                    <span style={{ fontSize: 14 }}>
                      <span style={{ display: "block" }}>{memberName}</span>
                      {car?.Car?.HoleLocation === 0
                        ? "Not playing"
                        : car?.Car?.HoleLocation === 1
                          ? `Hole ${car?.Car?.Hole} Between Holes `
                          : car?.Car?.HoleLocation === 2
                            ? `Hole ${car?.Car?.Hole} Tee`
                            : car?.Car?.HoleLocation === 3
                              ? `Hole ${car?.Car?.Hole} Fairway`
                              : car?.Car?.HoleLocation === 4
                                ? `Hole ${car?.Car?.Hole} Green`
                                : ""}
                    </span>
                    <br />
                    <span style={{ fontWeight: "bold" }}>
                      {t("Pace_Status")}: {" "}
                    </span>
                    <span style={{ fontSize: 14 }}>
                      {car?.Car?.HoleLocation !== 0
                        ? getPaceString(
                          car?.Car?.Pace,
                          car?.Car?.HolesPlayed,
                          car?.Car?.CarType
                        )
                        : t("Unknown")}
                      <br />
                      <span>
                        {car?.Car?.PlayerName1
                          ? `Playername : ${car?.Car?.PlayerName1}`
                          : t("Player_1")}
                      </span>
                      <br />
                      <span>
                        {car?.Car?.PlayerName2
                          ? `Playername : ${car?.Car?.PlayerName2}`
                          : t("Player_2")}
                      </span>
                      <br />
                      <span>
                        {t("Report_Reason")} :{" "}
                        {reasonForReportString(
                          car?.Car?.LocationReasonForReport
                        )}
                      </span>
                      <br />
                      <span>
                        {t("GPRS_Signal")} :{" "}
                        {car?.Car?.LocationGprsSignalStrength}{" "}
                      </span>
                      <br />
                      <span>
                        {t("Sequence")} # : {car?.Car?.LocationSequenceNumber}{" "}
                      </span>
                    </span>
                  </p>

                  <div className="icon-wrapper">
                    <Tooltip title={t("Send_Message")} placement="bottom">
                      <span
                        onClick={(e) => { setCarsData(e, GpsiCarId, carNumber); }}
                      >
                        <ChatIcon
                          width={18}
                          height={14}
                          color="var(--text-primary)"
                        />
                      </span>
                    </Tooltip>
                    <Tooltip title={t("Show_detail")} placement="bottom">
                      <span>
                        <ExclamationMarkWhiteIcon
                          color="var(--text-primary)"
                          onClick={handleShowDetail}
                        />
                      </span>
                    </Tooltip>
                    <Tooltip title={t("Find_car")} placement="bottom">
                      <span
                        className={
                          MakeHighlightTagOnMenu(carnumber) ? "active" : ""
                        }
                        onClick={() => {
                          setTagCarNumber(carnumber);
                          let getTaggedCar = JSON.parse(localStorage.getItem("isTaggedCar"));

                          if (getTaggedCar) {
                            let tempToggle = getTaggedCar.state === true ? { state: false, carNumber: carnumber } : { state: true, carNumber: carnumber };
                            localStorage.setItem("isTaggedCar", JSON.stringify(tempToggle));
                          } else {
                            let tempToggle = {
                              state: true,
                              carNumber: carnumber,
                            };
                            localStorage.setItem("isTaggedCar", JSON.stringify(tempToggle));
                          }
                        }}
                      >
                        <TagIcon
                          color="var(--text-primary)"
                        />
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={t("Show/Hide_locations")}
                      placement="bottom"
                    >
                      <span
                        className={
                          showTaggedCar.get() === carNumber ? "active" : ""
                        }
                        onClick={() => {
                          displayLocation.set((prevState) => !prevState);
                          clickWindowState.set(false);
                          localStorage.setItem(
                            "displayLocation",
                            displayLocation.get()
                          );
                          if (
                            showTaggedCar.get() &&
                            showTaggedCar.get() === carNumber
                          ) {
                            showTaggedCar.set(null);
                            specificCarTrackHistory.set(false);
                            CarLocationBtn.set(false);
                            let locationTrackingData = {
                              carNumber: null,
                              gpsiCarId: null,
                              carType: null,
                            };
                            locationTrackingData =
                              JSON.stringify(locationTrackingData);
                            localStorage.setItem(
                              "locationTrackingData",
                              locationTrackingData
                            );
                          } else {
                            showTaggedCar.set(carNumber);
                            let locationTrackingData = {
                              carNumber: carNumber,
                              gpsiCarId: GpsiCarId,
                              carType: carType,
                            };
                            locationTrackingData =
                              JSON.stringify(locationTrackingData);
                            localStorage.setItem(
                              "locationTrackingData",
                              locationTrackingData
                            );
                            showTaggedCar.set(carNumber);
                            let payload = {
                              gpsiCarId: GpsiCarId,
                              hoursBack: "72",
                            };
                            dispatch(
                              COMMON_SERVICES.getCarTrackServices(payload, siteId.get())
                            ).then(() => {
                              specificCarTrackHistory.set(true);
                              CarLocationBtn.set(true);
                            });
                          }
                        }}
                      >
                        <LocationPointIcon color="var(--text-primary)" />
                      </span>
                    </Tooltip>
                  </div>

                  <div className="arrow"></div>
                </div>
              </HoverWindow>
            )}
          </>
        ) : (
          <></>
        )}
      </CarWrapper>
    );
  }
);

const TaggedCarIconComponent = ({ uniqueId, paceStatus, GpsiCarId, CarNumber }) => {
  const { tagCarFromFleet, storeFleetTagCarData } = useHookstate(FLEET_GLOBAL_STATES);
  const { t } = useTranslation();
  const [hoverState, setHoverState] = useState(false);
  const [clickState, setClickState] = useState(false);
  const {
    showMessageBoxForSingleCar,
    getSingleCarData,
    tagCarNumber,
    carNumberHover,
  } = useHookstate(MAP_GLOBAL_STATES);

  const { CarLocationBtn } = useHookstate(CAR_LOCATION_STATE);

  const setTagCarNumber = (carnumber) => {
    if (tagCarNumber.get().length === 0) {
      tagCarNumber.set((tasks) => [...tasks, carnumber]);
    } else {
      const exists = tagCarNumber.get().find((item) => item === carnumber);
      if (exists) {
        tagCarNumber.set((tasks) => tasks.filter((task) => task !== carnumber));
      } else {
        tagCarNumber.set((tasks) => [...tasks, carnumber]);
      }
    }
  };

  function getDateInfo() {
    let dateData =
      storeFleetTagCarData.get() &&
      storeFleetTagCarData.get().LastLocationUpdateInUtc;
    const dateTimeSplitter = dateData && dateData.split("T");
    const dateSplitter = dateTimeSplitter && dateTimeSplitter[0].split("-");
    const monthFetcher = () => {
      switch (dateSplitter && dateSplitter[1]) {
        case "01" || "1":
          return "Jan";
        case "02" || "2":
          return "Feb";
        case "03" || "3":
          return "Mar";
        case "04" || "4":
          return "Apr";
        case "05" || "5":
          return "May";
        case "06" || "6":
          return "Jun";
        case "07" || "7":
          return "Jul";
        case "08" || "8":
          return "Aug";
        case "09" || "9":
          return "Sep";
        case "10":
          return "Oct";
        case "11":
          return "Nov";
        case "12":
          return "Dec";
        default:
          return "No Match";
      }
    };

    const dateFormat = `${dateSplitter && dateSplitter[2]}-${monthFetcher()}-${dateSplitter && dateSplitter[0]
      }`;
    const timeFetcher = dateTimeSplitter && dateTimeSplitter[1].split(".");
    const timeGetter = timeFetcher && timeFetcher[0].split(":");

    const timeFormat = () => {
      let hours = timeGetter && timeGetter[0];
      let minutes = timeGetter && timeGetter[1];
      let ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      minutes = minutes < 10 ? "0" + minutes : minutes;
      let strTime = hours + ":" + minutes + " " + ampm;
      return strTime;
    };
    return `${dateFormat}, ${timeFormat()}`;
  }

  const showWindow = () => {
    if (hoverState) {
      setHoverState(false);
      setClickState(!clickState);
    } else {
      setClickState(!clickState);
      carNumberHover.set(true);
    }
  };

  GpsiCarId = storeFleetTagCarData.get() && storeFleetTagCarData.get().GpsiCarId;
  CarNumber = storeFleetTagCarData.get() && storeFleetTagCarData.get().CarNumber;

  const setCarsData = (e, gpsiCarId, carNo) => {
    e.stopPropagation();
    showMessageBoxForSingleCar.set(true);
    getSingleCarData.set({ GpsiCarId: gpsiCarId, CarNumber: carNo });
  };

  return (
    <CarWrapper key={uniqueId}>
      <div
        className="container"
        onMouseEnter={() => {
          setHoverState(true);
          carNumberHover.set(true);
        }}
        onMouseLeave={() => {
          setHoverState(false);
          carNumberHover.set(false);
        }}
        onClick={showWindow}
        style={{
          border: storeFleetTagCarData.get().CarNumber
            ? "4px solid rgb(95,158,79)"
            : paceStatus === 0
              ? "4px solid #FFE500"
              : paceStatus < 0
                ? "4px solid #FA3C30"
                : "4px solid #fff",
        }}
      >
        <div className="car-icon">
          {tagCarFromFleet.get() && tagCarFromFleet.get()[0]?.cartype === 1 ? (
            <CarIcon />
          ) : tagCarFromFleet.get() &&
            tagCarFromFleet.get()[0]?.cartype === 2 ? (
            <MarshalCarIcon />
          ) : tagCarFromFleet.get() &&
            tagCarFromFleet.get()[0]?.cartype === 3 ? (
            <SpecialNeedCarIcons bg={"#0026AC"} />
          ) : tagCarFromFleet.get() &&
            tagCarFromFleet.get()[0]?.cartype === 4 ? (
            <FoodandBewragesCarIcon />
          ) : (
            <CarIcon color="black" />
          )}
        </div>
        <div className="car-number">
          {tagCarFromFleet.get() && tagCarFromFleet.get()[0]?.carno}
        </div>
      </div>
      {hoverState && !clickState ? (
        <HoverWindow>
          <div className="wrapper">
            <h3>
              Car:{" "}
              {storeFleetTagCarData.get() &&
                storeFleetTagCarData.get().CarNumber}
            </h3>
            <p className="date-and-time">{getDateInfo()}</p>
            <p>
              <span style={{ fontSize: 14 }}>
                {storeFleetTagCarData.get() &&
                  storeFleetTagCarData.get().Hole === 0
                  ? t("No_cars_available")
                  : `Hole ${storeFleetTagCarData.get().Hole}`}{" "}
              </span>
              <br />
              <span style={{ fontSize: 14 }}>
                <span style={{ fontWeight: "bold" }}>Status - </span>
                {storeFleetTagCarData.get() &&
                  storeFleetTagCarData.get().HoleLocation === 0
                  ? t("Unknown")
                  : storeFleetTagCarData.get().HoleLocation === 1
                    ? t("Between_Holes")
                    : storeFleetTagCarData.get().HoleLocation === 2
                      ? "Tee"
                      : storeFleetTagCarData.get().HoleLocation === 3
                        ? "Fairway"
                        : storeFleetTagCarData.get().HoleLocation === 4
                          ? t("Green")
                          : ""}
              </span>
              <br />
              <span style={{ fontSize: 14 }}>
                <span style={{ fontWeight: "bold" }}>
                  {t("Pace_Status")} 1-{" "}
                </span>
                {storeFleetTagCarData.get() &&
                  storeFleetTagCarData.get().Pace < 0
                  ? "Critical Behind Pace"
                  : storeFleetTagCarData.get() &&
                    storeFleetTagCarData.get().Pace === 0
                    ? "Behind Pace"
                    : storeFleetTagCarData.get() &&
                      storeFleetTagCarData.get().Pace > 0
                      ? "Pace ahead"
                      : ""}
              </span>
            </p>
            <div className="arrow"></div>
          </div>
        </HoverWindow>
      ) : (
        <></>
      )}
      {clickState && (
        <HoverWindow>
          <div className="wrapper">
            <h3>
              {t("Car")}:{" "}
              {storeFleetTagCarData.get() &&
                storeFleetTagCarData.get().CarNumber}
            </h3>
            <p className="date-and-time">{getDateInfo()}</p>
            <p>
              <span style={{ fontSize: 14 }}>
                {storeFleetTagCarData.get() &&
                  storeFleetTagCarData.get().Hole === 0
                  ? t("No_cars_available")
                  : `Hole ${storeFleetTagCarData.get().Hole}`}{" "}
              </span>
              <br />
              <span style={{ fontSize: 14 }}>
                <span style={{ fontWeight: "bold" }}>Status - </span>
                {storeFleetTagCarData.get() &&
                  storeFleetTagCarData.get().HoleLocation === 0
                  ? t("Unknown")
                  : storeFleetTagCarData.get().HoleLocation === 1
                    ? t("Between_Holes")
                    : storeFleetTagCarData.get().HoleLocation === 2
                      ? "Tee"
                      : storeFleetTagCarData.get().HoleLocation === 3
                        ? "Fairway"
                        : storeFleetTagCarData.get().HoleLocation === 4
                          ? t("Green")
                          : ""}
              </span>
              <br />
              <span style={{ fontSize: 14 }}>
                <span style={{ fontWeight: "bold" }}>
                  {t("Pace_Status")} 22 -{" "}
                </span>
                {storeFleetTagCarData.get() &&
                  storeFleetTagCarData.get().Pace < 0
                  ? "Critical Behind Pace"
                  : storeFleetTagCarData.get() &&
                    storeFleetTagCarData.get().Pace === 0
                    ? "Behind Pace"
                    : storeFleetTagCarData.get() &&
                      storeFleetTagCarData.get().Pace > 0
                      ? "Pace ahead"
                      : ""}
              </span>
            </p>
            <div className="icon-wrapper">
              <span onClick={(e) => setCarsData(e, GpsiCarId, CarNumber)}>
                <ChatIcon width={18} height={14} color="var(--text-primary)" />
              </span>
              <span>
                <ExclamationMarkWhiteIcon color="var(--text-primary)" />
              </span>
              <span onClick={() => setTagCarNumber(CarNumber)}>
                <TagIcon color="var(--text-primary)" />
              </span>
              <span onClick={() => CarLocationBtn.set(!CarLocationBtn.get())}>
                <LocationPointIcon color="var(--text-primary)" />
              </span>
            </div>
            <div className="arrow"></div>
          </div>
        </HoverWindow>
      )}
    </CarWrapper>
  );
};

export {
  MapHoleMarker,
  MapCarIcon,
  MapCenterMarker,
  TaggedCarIconComponent,
  MapPointMarker,
  MapTeesMarker,
};
